import React from 'react'
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material'
import WbButton from '../../../components/common/WbButton'
import WbModal from '../../../components/common/WbModal'
import {useState} from 'react'
import WbTextField from '../../../components/common/WbTextField'
import {useEffect} from 'react'
import {Add, MoreVert} from '@mui/icons-material'
import WbOutlineButton from '../../../components/common/WbOutlineButton'
import WbAlert from '../../../components/common/WbAlert'
import emailTemplateApi from '../../../services/api/emailTemplate/emailTemplateApi'
import ReactQuill, {Quill} from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import '../../../utils/quillSafeClipboard'
import {useRef} from 'react'
import DOMPurify from 'dompurify'
import {CUSTOMER_TYPE, DEFAULT_EMAIL_TEMPLATE} from '../../../utils/constants'
import useCustomerData from '../../../hooks/useCustomerData'
import themeLogoApi from '../../../services/api/themeLogo/themeLogoApi'
import {Config} from '../../../utils/Config'
import {buildInviteLogoPasteHtml} from './logoEmbed'
import {resolveSelectedTemplate} from './selectTemplate'

// Emit text alignment as an inline style (not a class) so centering — e.g. the logo
// header — survives into the sent email, which has no Quill stylesheet.
Quill.register(Quill.import('attributors/style/align'), true)

// Merge-field chip styling (Figma: blue outline, rounded 4px, blue medium 13px text).
const mergeChipSx = {
  height: 'auto',
  border: '1px solid #53b5e0',
  borderRadius: '4px',
  color: '#53b5e0',
  backgroundColor: 'transparent',
  '& .MuiChip-label': {
    padding: '8px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '16px',
  },
  '&:hover': {backgroundColor: 'rgba(83,181,224,0.08)'},
}

// "Add New Template" — Figma: blue (#53b5e0) filled, rounded 4px, + icon, white 14px semibold.
const addTemplateBtnSx = {
  textTransform: 'none',
  boxShadow: 'none',
  backgroundColor: '#53b5e0',
  borderRadius: '4px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  padding: '8px 12px',
  '&:hover': {backgroundColor: '#3ca5d4', boxShadow: 'none'},
}

// Figma: Subject + chips + editor live inside one #fafafa bordered card.
const cardSx = {
  position: 'relative',
  backgroundColor: '#fafafa',
  border: '1px solid #e2e2e2',
  borderRadius: '4px',
  mt: 1.5,
}

// Figma: the actions (Copy/Edit/Delete) collapse into a ⋮ kebab menu pinned to
// the top-right corner of the template preview card (read-only state only).
const kebabBtnSx = {
  position: 'absolute',
  top: 4,
  right: 4,
  zIndex: 2,
  color: '#54657e',
}

// Figma: the merge-field chips sit on a light grey band between Subject and editor.
const chipBandSx = {
  backgroundColor: '#f5f5f5',
  borderTop: '1px solid #f2f2f2',
  borderBottom: '1px solid #f2f2f2',
  px: '12px',
  py: '10px',
}

export default function CustomisedInvitations({
  utiliseTemplate,
  audience,
  survey,
  setSurvey,
}) {
  const getTemplateName = () => {
    return (
      new Date().toLocaleDateString().split('T')[0].replace(/-/g, '/') +
      '-' +
      (audience === 1 ? 'Staff' : audience === 2 ? 'Parent' : 'Student')
    )
  }

  const [showCustomisedInvitations, setShowCustomisedInvitations] =
    useState(false)
  const [getLinkText, setGetLinkText] = useState(false)
  const [selectionIndex, setSelectionIndex] = useState(0)
  const customer = useCustomerData()
  const [selected, setSelected] = useState(null)
  const [templates, setTemplates] = useState([])
  const [addNew, setAddNew] = useState(false)
  const [edit, setEdit] = useState(false)
  const [clone, setClone] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const closeMenu = () => setMenuAnchor(null)
  const [value, setValue] = useState('')
  const quillRef = useRef(null) 
  const [otherLink, setOtherLink] = useState(false)
  const [logoFileName, setLogoFileName] = useState('')
  const [OtherLinkText, setOtherLinkText] = useState('')
  const [OtherLinkhref, setOtherLinkhref] = useState('')
  const [newTemplate, setNewTemplate] = useState({
    id: 0,
    name: getTemplateName(),
    subject: '',
    message: '',
    audience: audience,
    matId: survey?.matId,
    schoolId: survey?.schoolId,
  })

  const handleLinkClick = () => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor()
      const selection = quill.getSelection()
      if (!selection || selection.length === 0) {
        setGetLinkText(true)
        setSelectionIndex(selection.index)
        return
      }
      let selectedText = null
      if (selection) {
        selectedText = quill.getText(selection.index, selection.length)
      }
      if (selectedText) {
        quill.formatText(
          selection.index,
          selection.length,
          'link',
          '{surveyLink}'
        )
      }
    }
  }

  const handlePersonNameClick = () => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor()

      const selection = quill.getSelection()
      quill.insertText(
        selection ? selection?.index : quill.getLength(),
        '{personFirstName}'
      )
    }
  }

  const handleTrustNameClick = () => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor()

      const selection = quill.getSelection()
      quill.insertText(
        selection ? selection?.index : quill.getLength(),
        '{schoolName}'
      )
    }
  }

  const handleLogoClick = () => {
    if (!logoFileName) {
      WbAlert({
        message:
          'No logo found. Add a logo to this survey theme first, then try again.',
        type: 'error',
      })
      return
    }
    if (quillRef.current) {
      const quill = quillRef.current.getEditor()
      const selection = quill.getSelection()
      const index = selection ? selection.index : quill.getLength()
      // Insert as a real <img> (kept by Quill's image blot, width preserved) so it
      // renders identically in the editor, both previews, and the sent email. The
      // paste builder wraps it in blank paragraphs so it stays a standalone centered
      // block even when inserted mid-paragraph.
      quill.clipboard.dangerouslyPasteHTML(
        index,
        buildInviteLogoPasteHtml(Config.BASE_URL, logoFileName),
        'user'
      )
      // Sync the embedded logo into template state immediately. Pasting via the
      // Quill API does not always fire the editor's change event, so without this
      // the <img> shows in the editor but is missing from the saved template.
      setNewTemplate(prev => ({...prev, message: quill.getSemanticHTML()}))
    }
  }

  // Re-sync the templates list AND the selected template whenever the audience
  // changes OR the survey's saved template for that audience changes (e.g. after
  // "Use Template" on another tab). Driven by survey.*EmailTemplateId so switching
  // tabs always restores the template that was applied for that audience.
  useEffect(() => {
    if (!survey) return
    emailTemplateApi
      .get(survey?.schoolId, survey?.matId, audience, survey.id)
      .then(list => {
        const fetched = [...list]
        setTemplates(fetched)
        const next = resolveSelectedTemplate(fetched, audience, survey)
        setSelected(next)
        setNewTemplate(next)
      })
    // Intentionally keyed on audience + the saved template ids only: we want to
    // re-sync on tab switch or when a template is applied, NOT on every survey
    // edit (subject/message/notification), which would refetch and reset selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    audience,
    survey?.staffEmailTemplateId,
    survey?.parentEmailTemplateId,
    survey?.pupilEmailTemplateId,
  ])

  // Load the trust/school default logo (set on the home Themes > Logos screen) so it can
  // be inserted into the invite. Prefer the school logo, fall back to the trust logo.
  useEffect(() => {
    if (!survey?.id) return
    themeLogoApi
      .resolveSurveyLogoFileName(survey.schoolId, survey.matId)
      .then(file => setLogoFileName(file || ''))
      .catch(() => setLogoFileName(''))
  }, [survey?.id])

  const updateAfterSave = e => {
    WbAlert({
      message: 'Template saved',
      type: 'success',
    })
    setClone(false)
    setAddNew(false)
    setEdit(false)
    let newList = [...templates, e]
    setTemplates(newList)
    setSelected(newList[newList.length - 1])
    setNewTemplate(newList[newList.length - 1])
  }
  const [linkText, setLinkText] = useState('')

  const doSave = () => {
    setGetLinkText(false)
    if (newTemplate.subject === null || newTemplate.subject === '') {
      WbAlert({
        message: 'Please enter a subject',
        type: 'error',
      })
      return false
    }
    if (newTemplate.message === null || newTemplate.message === '') {
      WbAlert({
        message: 'Please enter a message',
        type: 'error',
      })
      return false
    }

    if (newTemplate.message.indexOf('{surveyLink}') === -1) {
      WbAlert({
        message: 'Please insert a survey link',
        type: 'error',
      })
      return false
    }
    if (newTemplate.id === 0) {
      emailTemplateApi.post(newTemplate).then(e => updateAfterSave(e))
    }
    if (newTemplate.id > 0) {
      emailTemplateApi.put(newTemplate).then(e => {
        WbAlert({
          message: 'Template saved',
          type: 'success',
        })
        setTemplates([
          ...templates.filter(x => x.id !== newTemplate.id),
          newTemplate,
        ])
        setSelected({...newTemplate})
      })
    }
    return true
  }

  return (
    <Grid container>
      <Grid item lg={12} md={12} sm={12} xs={12} style={{textAlign: 'right'}}>
        <WbButton
          CustomButtonText={'Customise Invitations'}
          onClick={() => {
            setShowCustomisedInvitations(true)
          }}
        />
      </Grid>

      {getLinkText && (
        <WbModal
          onClose={() => {
            setGetLinkText(false)
          }}
          title={'Survey Link'}
          content={
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <WbTextField
                  label="Enter text to appear on survey link here..."
                  value={linkText}
                  onChange={e => setLinkText(e.target.value)}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12} textAlign="right">
                <WbOutlineButton
                  CustomOutlineButtonText={'Cancel'}
                  onClick={e => setGetLinkText(false)}
                />
                {'  '}
                <WbButton
                  CustomButtonText={'Insert'}
                  onClick={e => {
                    setGetLinkText(false)

                    if (quillRef.current) {
                      const quill = quillRef.current.getEditor()
                      const selection = quill.getSelection()

                      quill.insertText(
                        selectionIndex ? selectionIndex : quill.getLength(),
                        linkText,
                        'link',
                        '{surveyLink}'
                      )
                      setLinkText('')
                      setSelectionIndex(0)
                    }
                  }}
                />
              </Grid>
            </Grid>
          }
        ></WbModal>
      )}
        {otherLink && (
        <WbModal
          onClose={() => {
            setOtherLink(false)
            setOtherLinkText('')
            setOtherLinkhref('')
          }}
          title={'Add a link'}
          content={
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <WbTextField
                  label="Enter text to appear for url here..."
                  value={OtherLinkText}
                  onChange={e => setOtherLinkText(e.target.value)}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <WbTextField
                  label="Enter url here..."
                  value={OtherLinkhref}
                  onChange={e => setOtherLinkhref(e.target.value)}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12} textAlign="right">
                <WbOutlineButton
                  CustomOutlineButtonText={'Cancel'}
                  onClick={e => {
                    setOtherLink(false)
                    setOtherLinkText('')
                    setOtherLinkhref('')
                  }}
                />
                {'  '}
                <WbButton
                  CustomButtonText={'Insert'}
                  onClick={e => {
                    if (OtherLinkText === '' || OtherLinkhref === '') {
                      alert('Please fill in all fields')
                      return
                    }
                    try {
                      var correct = Boolean(new URL(OtherLinkhref))
                      if (!correct) {
                        alert('Invalid URL')
                        return false
                      }
                    } catch (e) {
                      alert('Invalid URL')
                      return false
                    }

                    setOtherLink(false)
                    

                    if (quillRef.current) {
                      const quill = quillRef.current.getEditor()
                      const selection = quill.getSelection()

                      quill.insertText(
                        selectionIndex ? selectionIndex : quill.getLength(),
                        OtherLinkText,
                        'link',
                        OtherLinkhref
                      )
                      setOtherLinkText('')
                      setOtherLinkhref('')
                    }
                  }}
                />
              </Grid>
            </Grid>
          }
        ></WbModal>
      )}
        {otherLink && (
        <WbModal
          onClose={() => {
            setOtherLink(false)
            setOtherLinkText('')
            setOtherLinkhref('')
          }}
          title={'Add a link'}
          content={
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <WbTextField
                  label="Enter text to appear for url here..."
                  value={OtherLinkText}
                  onChange={e => setOtherLinkText(e.target.value)}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <WbTextField
                  label="Enter url here..."
                  value={OtherLinkhref}
                  onChange={e => setOtherLinkhref(e.target.value)}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12} textAlign="right">
                <WbOutlineButton
                  CustomOutlineButtonText={'Cancel'}
                  onClick={e => {
                    setOtherLink(false)
                    setOtherLinkText('')
                    setOtherLinkhref('')
                  }}
                />
                {'  '}
                <WbButton
                  CustomButtonText={'Insert'}
                  onClick={e => {
                    if (OtherLinkText === '' || OtherLinkhref === '') {
                      alert('Please fill in all fields')
                      return
                    }
                    try {
                      var correct = Boolean(new URL(OtherLinkhref))
                      if (!correct) {
                        alert('Invalid URL')
                        return false
                      }
                    } catch (e) {
                      alert('Invalid URL')
                      return false
                    }

                    setOtherLink(false)

                    if (quillRef.current) {
                      const quill = quillRef.current.getEditor()
                      const selection = quill.getSelection()

                      quill.insertText(
                        selectionIndex ? selectionIndex : quill.getLength(),
                        OtherLinkText,
                        'link',
                        OtherLinkhref
                      )
                      setOtherLinkText('')
                      setOtherLinkhref('')
                    }
                  }}
                />
              </Grid>
            </Grid>
          }
        ></WbModal>
      )}
      {showCustomisedInvitations && (
        <WbModal
          onClose={() => {
            setAddNew(false)
            setShowCustomisedInvitations(false)
            setEdit(false)
          }}
          title={'Invitation Template'}
          maxWidth={'md'}
          content={
            <>
              <Grid container justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  disableElevation
                  disableRipple
                  sx={addTemplateBtnSx}
                  onClick={() => {
                    setGetLinkText(false)
                    setAddNew(true)
                    setEdit(false)
                    setClone(false)
                    setNewTemplate({
                      id: 0,
                      name: getTemplateName(),
                      subject: DEFAULT_EMAIL_TEMPLATE.subject,
                      message: DEFAULT_EMAIL_TEMPLATE.message,
                      audience: audience,
                      matId: survey?.matId,
                      schoolId: survey?.schoolId,
                    })
                  }}
                >
                  Add New Template
                </Button>
              </Grid>
              <Grid container mt={1.5}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <WbTextField
                      fullWidth
                      select
                      labelId="template-select-id"
                      id="template-select"
                      label="Select Template"
                      value={selected}
                      renderValue={selected => {
                        return selected?.subject
                      }}
                      onChange={e => {
                        var sel = templates.findIndex(
                          x => x.id === e.target.value.id
                        )

                        setSelected(templates[sel])
                        setNewTemplate(templates[sel])
                        setAddNew(false)
                        setEdit(false)
                        setClone(false)
                      }}
                    >
                      {templates.map(option => (
                        <MenuItem key={option?.id} value={option}>
                          {option.subject}
                        </MenuItem>
                      ))}
                    </WbTextField>
                  </FormControl>
                </Grid>
              </Grid>
              {/* Figma: Subject, merge chips and editor sit inside one #fafafa card. */}
              <Box sx={cardSx}>
                {addNew || edit || clone ? (
                  <>
                    <Box sx={{px: '12px', pt: '12px'}}>
                      <WbTextField
                        label="Subject"
                        value={newTemplate?.subject}
                        onChange={e => {
                          setNewTemplate({
                            ...newTemplate,
                            subject: e.target.value,
                          })
                        }}
                      ></WbTextField>
                    </Box>
                    {/* Merge-field chips on a grey band, below Subject (Figma). */}
                    <Box sx={chipBandSx}>
                      <Stack
                        spacing={0.875}
                        direction="row"
                        justifyContent="flex-start"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Chip
                          clickable
                          variant="outlined"
                          sx={mergeChipSx}
                          label={`${customer?.customerType === CUSTOMER_TYPE.MAT ? 'Trust' : 'School'} Logo`}
                          onClick={handleLogoClick}
                        />
                        <Chip
                          clickable
                          variant="outlined"
                          sx={mergeChipSx}
                          label={`${customer?.customerType === CUSTOMER_TYPE.MAT ? 'Trust' : 'School'} Name`}
                          onClick={handleTrustNameClick}
                        />
                        <Chip
                          clickable
                          variant="outlined"
                          sx={mergeChipSx}
                          label="Participant Name"
                          onClick={handlePersonNameClick}
                        />
                        <Chip
                          clickable
                          variant="outlined"
                          sx={mergeChipSx}
                          label="Survey Link"
                          onClick={handleLinkClick}
                        />
                        <Chip
                          clickable
                          variant="outlined"
                          sx={mergeChipSx}
                          label="External Link"
                          onClick={() => setOtherLink(true)}
                        />
                      </Stack>
                    </Box>
                    <Box sx={{p: '12px'}}>
                      {/* Match the editor's paragraph spacing to the sent email
                          (and previews), which render <p> with default margins.
                          Without this, Quill shows lines tight, users add blank
                          lines, and the email ends up double-spaced. */}
                      <style>{`
                        .invite-message-editor .ql-editor p { margin: 1em 0; }
                        .invite-message-editor .ql-editor img { max-width: 100%; height: auto; }
                        .invite-message-editor .ql-container, .invite-message-editor .ql-toolbar { background: #fff; }
                      `}</style>
                      <ReactQuill
                        className="invite-message-editor"
                        theme="snow"
                        ref={quillRef}
                        modules={{
                          toolbar: [
                            [{header: [1, 2, false]}],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{list: 'ordered'}, {list: 'bullet'}],
                          ],
                        }}
                        value={newTemplate.message}
                        placeholder="Message"
                        style={{height: '250px', marginBottom: '25px'}}
                        onChange={e => {
                          var newTemp = {...newTemplate}
                          newTemp.message = e
                          setNewTemplate({...newTemp})
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Figma: Copy / Edit / Delete live in a ⋮ kebab menu pinned
                        to the top-right corner of the preview card. */}
                    <IconButton
                      aria-label="Template actions"
                      sx={kebabBtnSx}
                      onClick={e => setMenuAnchor(e.currentTarget)}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={closeMenu}
                      anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                      transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    >
                      <MenuItem
                        onClick={() => {
                          closeMenu()
                          setGetLinkText(false)
                          setNewTemplate({
                            ...selected,
                            id: 0,
                            audience: audience,
                          })
                          setClone(true)
                          setAddNew(false)
                          setEdit(false)
                        }}
                      >
                        Copy
                      </MenuItem>
                      <MenuItem
                        disabled={selected?.id === -1}
                        onClick={() => {
                          closeMenu()
                          setGetLinkText(false)
                          setAddNew(false)
                          setEdit(true)
                          setClone(false)
                          setNewTemplate({...selected})
                        }}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        disabled={selected?.id === -1}
                        onClick={() => {
                          closeMenu()
                          emailTemplateApi
                            .delete(selected.id)
                            .then(e => setConfirmDelete(true))
                        }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                    <Box sx={{p: '12px'}}>
                      <div style={{fontWeight: 600, marginBottom: '8px'}}>
                        Email Subject: {selected?.subject}
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            (selected?.message || '').replace(
                              '{schoolName}',
                              customer?.name || ''
                            )
                          ),
                        }}
                        style={{
                          height: '250px',
                          overflow: 'auto',
                          background: '#fff',
                          border: '1px solid #e2e2e2',
                          padding: '12px',
                        }}
                      ></div>
                    </Box>
                  </>
                )}
              </Box>

              {confirmDelete && (
                <WbModal
                  onClose={() => setConfirmDelete(false)}
                  title={'Confirm Delete'}
                  maxWidth={'sm'}
                  content={
                    <Stack spacing={2}>
                      <Stack spacing={2}>
                        <p>Are you sure you want to delete this template?</p>
                      </Stack>
                      <Stack
                        spacing={2}
                        direction="row"
                        style={{float: 'right'}}
                      >
                        <WbOutlineButton
                          CustomOutlineButtonText={'Cancel'}
                          onClick={() => setConfirmDelete(false)}
                        />
                        <WbButton
                          CustomButtonText={'Delete'}
                          onClick={() => {
                            setAddNew(false)
                            setEdit(false)
                            setClone(false)
                            setConfirmDelete(false)
                            setNewTemplate({
                              id: 0,
                              name: getTemplateName(),
                              subject: '',
                              message: '',
                              audience: audience,
                              matId: survey?.matId,
                              schoolId: survey?.schoolId,
                            })

                            WbAlert({
                              message: 'Template deleted',
                              type: 'success',
                            })
                            setTemplates(
                              templates.filter(e => e.id !== selected?.id)
                            )
                            setSelected(templates[0])
                          }}
                        />
                      </Stack>
                    </Stack>
                  }
                />
              )}

              {/* Figma footer: Save (left) · Cancel + Use Template (right). */}
              <Grid container mt={2} alignItems="center">
                <Grid item xs={6}>
                  {(addNew || clone || edit) && (
                    <WbOutlineButton
                      disabled={
                        newTemplate?.subject === '' ||
                        newTemplate?.message === ''
                      }
                      CustomOutlineButtonText={'Save'}
                      onClick={() => doSave()}
                    />
                  )}
                </Grid>
                <Grid item xs={6} style={{textAlign: 'right'}}>
                  <WbOutlineButton
                    CustomOutlineButtonText={'Cancel'}
                    onClick={() => {
                      setShowCustomisedInvitations(false)
                      setAddNew(false)
                      setEdit(false)
                      setClone(false)
                    }}
                  />{' '}
                  <WbButton
                    disabled={
                      newTemplate?.subject === '' || newTemplate?.message === ''
                    }
                    CustomButtonText={'Use Template'}
                    onClick={e => {
                      // Capture the template being applied before any state updates.
                      const applied = newTemplate
                      // Merge the saved id functionally so the parent's notification
                      // effect (which also calls setSurvey) can't clobber it.
                      setSurvey(prev => {
                        const next = {...prev}
                        if (audience === 1)
                          next.staffEmailTemplateId = applied.id
                        if (audience === 2)
                          next.parentEmailTemplateId = applied.id
                        if (audience === 4)
                          next.pupilEmailTemplateId = applied.id
                        return next
                      })
                      if (doSave()) {
                        setGetLinkText(false)
                        setSelected(applied)
                        setShowCustomisedInvitations(false)
                        setAddNew(false)
                        setEdit(false)
                        setClone(false)
                        utiliseTemplate(applied)
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </>
          }
        />
      )}
    </Grid>
  )
}
